// ---------------------------------------------------------------------------
// shacl-compiler — compiles SHACL NodeShapes from a TTL file into
// `ShaclQueryRule[]` that kg-browser can execute via `applyShacl`.
//
// Design points:
//   - Build-time only. Callers run this under Node (tsx / script) against
//     the ontology TTL (e.g. `cogo-ns-v1.ttl`) and emit a static TS file.
//   - Supports a pragmatic subset: `sh:minCount`, `sh:maxCount`,
//     `sh:datatype`, `sh:in`, `sh:pattern`, `sh:class`. These cover the
//     constraints in cogo-ns-v1.ttl that the SPARQL runner can execute.
//     More advanced constraints (`sh:or`, `sh:node`) are skipped — they
//     compile to more complex SPARQL and are left for a later compiler
//     pass.
//   - Each property constraint → one `ShaclQueryRule`. SHACL's "one
//     violation per property per focus node" model maps cleanly to SPARQL
//     SELECT returning (?focus, ?value) rows.
//   - IRI namespace pinned to `https://cogo.xyz/ns/v1#`. Other namespaces
//     (rdf / rdfs / xsd) use standard prefixes emitted in every rule.
//
// Referenced by:
//   - scripts/compile-shacl.ts (CLI entry)
//   - src/generated/cogo-ns-v1-shacl-rules.ts (emitted output, checked in)
//
// Spec ref: `docs/agentic/architecture/kg-repository-common.md` §5.1 #6.
// ---------------------------------------------------------------------------

import { Parser, Store, DataFactory } from "n3"
import type { Quad, NamedNode, Term } from "n3"

import type { ShaclQueryRule } from "@cogo/kg-browser"

const { namedNode } = DataFactory

// ——— Namespace constants ———

const SH = "http://www.w3.org/ns/shacl#"
const RDF = "http://www.w3.org/1999/02/22-rdf-syntax-ns#"
const RDFS = "http://www.w3.org/2000/01/rdf-schema#"
const XSD = "http://www.w3.org/2001/XMLSchema#"
const COGO = "https://cogo.xyz/ns/v1#"

function sh(local: string): NamedNode {
  return namedNode(SH + local)
}

function rdf(local: string): NamedNode {
  return namedNode(RDF + local)
}

function rdfs(local: string): NamedNode {
  return namedNode(RDFS + local)
}

// ——— Internal parsed shape representation ———

/** Short "cogo:xxx" form for display + SPARQL PREFIX hydration. */
function toPrefixed(iri: string): string {
  if (iri.startsWith(COGO)) return "cogo:" + iri.slice(COGO.length)
  if (iri.startsWith(XSD)) return "xsd:" + iri.slice(XSD.length)
  if (iri.startsWith(RDFS)) return "rdfs:" + iri.slice(RDFS.length)
  return "<" + iri + ">"
}

/** The cogo-ns prefix short-name for a URL (e.g. "Component" from cogo:Component). */
function localName(iri: string): string {
  const hashIdx = iri.lastIndexOf("#")
  if (hashIdx >= 0) return iri.slice(hashIdx + 1)
  const slashIdx = iri.lastIndexOf("/")
  return slashIdx >= 0 ? iri.slice(slashIdx + 1) : iri
}

interface PropertyConstraint {
  /** `cogo:xxx` (prefixed) or `<...>` form for SPARQL. */
  pathPrefixed: string
  /** Full IRI of the property. */
  pathIri: string
  minCount?: number
  maxCount?: number
  /** `xsd:string` etc (prefixed). */
  datatypePrefixed?: string
  /** Enum values as plain strings (lexical forms). */
  inValues?: string[]
  /** Regex pattern (SPARQL regex flavor). */
  pattern?: string
  /** `cogo:Pattern` etc (prefixed) for `sh:class`. */
  classPrefixed?: string
}

interface ParsedNodeShape {
  /** Shape IRI (e.g. cogo:ComponentShape). */
  shapeIri: string
  /** Short shape name ("ComponentShape"). */
  shapeName: string
  /** Target class IRI (e.g. cogo:Component). */
  targetClassIri: string
  /** Short target class name ("Component"). */
  targetClassName: string
  /** Property constraints. */
  properties: PropertyConstraint[]
}

// ——— Public API ———

export interface CompileShaclOptions {
  /** Optional source label for rule shape names. Default: "cogo-ns". */
  sourceLabel?: string
}

/** Compile a TTL source string into `ShaclQueryRule[]`. */
export function compileShacl(
  ttl: string,
  opts: CompileShaclOptions = {},
): ShaclQueryRule[] {
  const quads = parseTtl(ttl)
  const store = new Store(quads)
  const shapes = extractNodeShapes(store)
  const rules: ShaclQueryRule[] = []
  for (const shape of shapes) {
    for (const prop of shape.properties) {
      rules.push(...emitRulesForProperty(shape, prop, opts))
    }
  }
  return rules
}

/** Expose parsed shape summary — useful for tests + introspection. */
export function parseShapes(ttl: string): ParsedNodeShape[] {
  const store = new Store(parseTtl(ttl))
  return extractNodeShapes(store)
}

// ——— Internals ———

function parseTtl(ttl: string): Quad[] {
  const parser = new Parser({ format: "text/turtle" })
  return parser.parse(ttl)
}

/** Extract cogo:XShape nodes with sh:targetClass + sh:property blocks. */
function extractNodeShapes(store: Store): ParsedNodeShape[] {
  const nodeShapeType = sh("NodeShape")
  const targetClassPred = sh("targetClass")
  const propertyPred = sh("property")

  const out: ParsedNodeShape[] = []
  const shapeSubjects = store.getSubjects(rdf("type"), nodeShapeType, null)

  for (const subject of shapeSubjects) {
    if (subject.termType !== "NamedNode") continue

    const targetClassQuads = store.getQuads(subject, targetClassPred, null, null)
    if (targetClassQuads.length === 0) continue
    const targetClassNode = targetClassQuads[0].object
    if (targetClassNode.termType !== "NamedNode") continue

    const properties: PropertyConstraint[] = []
    for (const propQuad of store.getQuads(subject, propertyPred, null, null)) {
      const propNode = propQuad.object as Term
      // sh:property object is typically a blank node; occasionally a named node.
      if (propNode.termType !== "BlankNode" && propNode.termType !== "NamedNode") continue
      const parsed = readProperty(store, propNode as NamedNode)
      if (parsed) properties.push(parsed)
    }

    out.push({
      shapeIri: subject.value,
      shapeName: localName(subject.value),
      targetClassIri: targetClassNode.value,
      targetClassName: localName(targetClassNode.value),
      properties,
    })
  }

  // Stable order by target class name for deterministic output.
  out.sort((a, b) => a.targetClassName.localeCompare(b.targetClassName))
  for (const s of out) {
    s.properties.sort((a, b) => a.pathPrefixed.localeCompare(b.pathPrefixed))
  }
  return out
}

function readProperty(store: Store, propNode: NamedNode): PropertyConstraint | null {
  // propNode is a blank node or named node; both work with store lookups.
  const getOne = (pred: NamedNode): string | undefined => {
    const quads = store.getQuads(propNode, pred, null, null)
    return quads.length > 0 ? quads[0].object.value : undefined
  }

  const pathIri = getOne(sh("path"))
  if (!pathIri) return null

  const minCountStr = getOne(sh("minCount"))
  const maxCountStr = getOne(sh("maxCount"))
  const datatypeIri = getOne(sh("datatype"))
  const pattern = getOne(sh("pattern"))
  const classIri = getOne(sh("class"))

  // sh:in — rdf:List
  const inValues: string[] = []
  const inHeadQuads = store.getQuads(propNode, sh("in"), null, null)
  if (inHeadQuads.length > 0) {
    let cursor: Term = inHeadQuads[0].object
    while (
      cursor &&
      (cursor.termType === "BlankNode" || cursor.termType === "NamedNode") &&
      !(cursor.termType === "NamedNode" && cursor.value === RDF + "nil")
    ) {
      const firstQuads = store.getQuads(cursor as NamedNode, rdf("first"), null, null)
      if (firstQuads.length === 0) break
      inValues.push(firstQuads[0].object.value)
      const restQuads = store.getQuads(cursor as NamedNode, rdf("rest"), null, null)
      if (restQuads.length === 0) break
      cursor = restQuads[0].object as Term
    }
  }

  return {
    pathPrefixed: toPrefixed(pathIri),
    pathIri,
    minCount: minCountStr !== undefined ? Number(minCountStr) : undefined,
    maxCount: maxCountStr !== undefined ? Number(maxCountStr) : undefined,
    datatypePrefixed: datatypeIri ? toPrefixed(datatypeIri) : undefined,
    inValues: inValues.length > 0 ? inValues : undefined,
    pattern,
    classPrefixed: classIri ? toPrefixed(classIri) : undefined,
  }
}

// ——— Rule emission ———

const SPARQL_HEADER = `PREFIX rdf: <${RDF}>
PREFIX rdfs: <${RDFS}>
PREFIX xsd: <${XSD}>
PREFIX cogo: <${COGO}>`

function ruleShape(shape: ParsedNodeShape, prop: PropertyConstraint, tag: string): string {
  return `cogo:${shape.shapeName}#${localName(prop.pathIri)}-${tag}`
}

function emitRulesForProperty(
  shape: ParsedNodeShape,
  prop: PropertyConstraint,
  opts: CompileShaclOptions,
): ShaclQueryRule[] {
  const rules: ShaclQueryRule[] = []
  const target = `cogo:${shape.targetClassName}`
  const path = prop.pathPrefixed

  if (prop.minCount !== undefined && prop.minCount >= 1) {
    rules.push({
      shape: ruleShape(shape, prop, "min-count"),
      focusClass: shape.targetClassName,
      sparql: `${SPARQL_HEADER}
SELECT ?focus WHERE {
  ?focus a ${target} .
  FILTER NOT EXISTS { ?focus ${path} ?_ . }
}
LIMIT 20`,
      ask: false,
      severity: "violation",
      message: `${shape.targetClassName} is missing ${path} (minCount=${prop.minCount})`,
    })
  }

  if (prop.maxCount !== undefined) {
    rules.push({
      shape: ruleShape(shape, prop, "max-count"),
      focusClass: shape.targetClassName,
      sparql: `${SPARQL_HEADER}
SELECT ?focus (COUNT(?v) AS ?count) WHERE {
  ?focus a ${target} ; ${path} ?v .
}
GROUP BY ?focus
HAVING (?count > ${prop.maxCount})
LIMIT 20`,
      ask: false,
      severity: "violation",
      message: `${shape.targetClassName} has more than ${prop.maxCount} ${path}`,
    })
  }

  if (prop.datatypePrefixed) {
    rules.push({
      shape: ruleShape(shape, prop, "datatype"),
      focusClass: shape.targetClassName,
      sparql: `${SPARQL_HEADER}
SELECT ?focus ?v WHERE {
  ?focus a ${target} ; ${path} ?v .
  FILTER(DATATYPE(?v) != ${prop.datatypePrefixed})
}
LIMIT 20`,
      ask: false,
      severity: "violation",
      message: `${shape.targetClassName}.${localName(prop.pathIri)} must be ${prop.datatypePrefixed}`,
    })
  }

  if (prop.inValues && prop.inValues.length > 0) {
    const inList = prop.inValues.map((v) => JSON.stringify(v)).join(", ")
    rules.push({
      shape: ruleShape(shape, prop, "in"),
      focusClass: shape.targetClassName,
      sparql: `${SPARQL_HEADER}
SELECT ?focus ?v WHERE {
  ?focus a ${target} ; ${path} ?v .
  FILTER(STR(?v) NOT IN (${inList}))
}
LIMIT 20`,
      ask: false,
      severity: "violation",
      message: `${shape.targetClassName}.${localName(prop.pathIri)} must be one of [${prop.inValues.join(", ")}]`,
    })
  }

  if (prop.pattern) {
    // Escape backslashes + double-quotes for embedding in SPARQL string literal.
    const safePattern = prop.pattern.replace(/\\/g, "\\\\").replace(/"/g, '\\"')
    rules.push({
      shape: ruleShape(shape, prop, "pattern"),
      focusClass: shape.targetClassName,
      sparql: `${SPARQL_HEADER}
SELECT ?focus ?v WHERE {
  ?focus a ${target} ; ${path} ?v .
  FILTER(!REGEX(STR(?v), "${safePattern}"))
}
LIMIT 20`,
      ask: false,
      severity: "violation",
      message: `${shape.targetClassName}.${localName(prop.pathIri)} must match /${prop.pattern}/`,
    })
  }

  if (prop.classPrefixed) {
    rules.push({
      shape: ruleShape(shape, prop, "class"),
      focusClass: shape.targetClassName,
      sparql: `${SPARQL_HEADER}
SELECT ?focus ?v WHERE {
  ?focus a ${target} ; ${path} ?v .
  FILTER NOT EXISTS { ?v a ${prop.classPrefixed} }
}
LIMIT 20`,
      ask: false,
      severity: "violation",
      message: `${shape.targetClassName}.${localName(prop.pathIri)} must be a ${prop.classPrefixed}`,
    })
  }

  void opts
  return rules
}

// ——— Helpers for generated output ———

/** Summary line used in the generated file header. */
export function summarizeRules(rules: ShaclQueryRule[]): string {
  const counts: Record<string, number> = {}
  for (const r of rules) {
    const tag = r.shape.split("#").pop()?.split("-").slice(-2).join("-") ?? "unknown"
    counts[tag] = (counts[tag] ?? 0) + 1
  }
  return Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([tag, n]) => `${tag}: ${n}`)
    .join(", ")
}
